import api, { webApi, getCsrfToken, backendOrigin } from "../api";

export interface User {
  id?: number;
  // support both fullname 'name' and split names
  name?: string;
  firstname?: string;
  lastname?: string;
  email: string;
  role?: string;
  password?: string;
  phone?: string;
  dob?: string;
  age?: number;
  age_grade_id?: number;
  village?: string;
  created_at?: string;
  pfp?: string; // profile photo path
}

export const getUsers = async (params?: any): Promise<any> => {
  try {
    const res = await webApi.get(`/api/admin/users`, { params });
    return res.data;
  } catch (err: any) {
    // Log helpful debug info for network / server errors
    // eslint-disable-next-line no-console
    console.error("getUsers error", {
      url: `${backendOrigin}/api/admin/users`,
      params,
      message: err?.message,
      status: err?.response?.status,
      data: err?.response?.data,
      config: err?.config,
    });
    throw err;
  }
};

export const getUser = async (id: number): Promise<any> => {
  const res = await webApi.get(`/api/admin/users/${id}`);
  return res.data;
};

export const createUser = async (payload: Partial<User>): Promise<User> => {
  await getCsrfToken();
  const res = await webApi.post(`/api/admin/users`, payload);
  return res.data;
};

export const updateUser = async (
  id: number,
  payload: Partial<User>
): Promise<User> => {
  await getCsrfToken();
  const res = await webApi.put(`/api/admin/users/${id}`, payload);
  return res.data;
};

// Update the currently authenticated user's profile
export const updateCurrentUser = async (
  payload: Partial<User> | FormData
): Promise<any> => {
  await getCsrfToken();
  // If sending FormData, PHP/Laravel may not parse multipart on PUT requests.
  // Use POST with method override instead: append _method=PUT and POST the FormData.
  if (payload instanceof FormData) {
    // add method override (avoid duplicate)
    if (!payload.has("_method")) payload.append("_method", "PUT");
    // Debug: log form data entries (can help inspect in devtools)
    try {
      for (const pair of payload.entries()) {
        // eslint-disable-next-line no-console
        console.log("FormData", pair[0], pair[1]);
      }
    } catch (e) {
      /* ignore */
    }
    const res = await webApi.post(`/user`, payload);
    return res.data;
  }

  const res = await webApi.put(`/user`, payload as Partial<User>);
  return res.data;
};

// Delete current user's profile photo
export const deleteCurrentUserPfp = async (): Promise<any> => {
  await getCsrfToken();
  const res = await webApi.delete(`/user/pfp`);
  return res.data;
};

// Update current user's password
export const updateCurrentUserPassword = async (payload: {
  current_password: string;
  password: string;
  password_confirmation: string;
}): Promise<any> => {
  await getCsrfToken();
  const res = await webApi.put(`/profile/password`, payload);
  return res.data;
};

export const deleteUser = async (id: number): Promise<void> => {
  await getCsrfToken();
  await webApi.delete(`/api/admin/users/${id}`);
};

export const changeUserRole = async (id: number, role: string) => {
  await getCsrfToken();
  const res = await webApi.put(`/api/admin/users/${id}/role`, { role });
  return res.data;
};

export const adminResetPassword = async (id: number) => {
  await getCsrfToken();
  const res = await webApi.post(`/api/admin/users/${id}/reset-password`);
  return res.data;
};

export const bulkDeleteUsers = async (ids: number[]) => {
  await getCsrfToken();
  const res = await webApi.post(`/api/admin/users/bulk-delete`, { ids });
  return res.data;
};

export const bulkChangeRoles = async (ids: number[], role: string) => {
  await getCsrfToken();
  const res = await webApi.post(`/api/admin/users/bulk-role`, { ids, role });
  return res.data;
};

export default {
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  changeUserRole,
  adminResetPassword,
  bulkDeleteUsers,
  bulkChangeRoles,
};

// Re-export backendOrigin from api for convenience to callers that import from services
export { backendOrigin };
